import { bind, classNames, Component, h, signal, tag } from "omi";

import { NWButton } from "@/wc/components/atoms/nw-button";
import { NWDropdown } from "@/wc/components/atoms/nw-dropdown";
import { NoteType } from "@/wc/editor/components/nw-note/models.ts";
import { type EditorController } from "@/wc/editor/controller.ts";
import { useEditorController } from "@/wc/editor/useEditor.ts";
import { openModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { HighlightColors, NoteActions, NoteActionToShortcut, ShortcutToNoteAction } from "../models.ts";
import { getHighlightOptions, getNoteOptions } from "../utils.ts";
import { AskView } from "./views/ask";

type Props = {
  group?: string;
};

@tag("nw-highlight-toolbar")
export class NWHighlightToolbar extends Component<Props> {
  static css = [tailwind];
  static propTypes = {
    group: String,
  };

  private highlightOptions = getHighlightOptions(this.addOrChangeHighlightColor);
  private noteOptions = getNoteOptions(this.handleNoteAction);
  private activeAction = signal<NoteActions | undefined>(undefined);

  installed() {
    addEventListener("keydown", this.handleKeyPress);
  }

  uninstall() {
    this.activeAction.value = undefined;
    removeEventListener("keydown", this.handleKeyPress);
  }

  @bind
  private handleKeyPress(event: KeyboardEvent): void {
    // Only handle keypresses when toolbar is active
    if (!Store.editor.isToolbarActive()) return;

    // Catch just in case for when the event listener is not removed when the view changes.
    if (this.activeAction.value === NoteActions.ASK) {
      return;
    }

    const key = event.key.toLowerCase();
    const action = ShortcutToNoteAction[key];

    if (action) {
      event.preventDefault();
      this.handleNoteAction(action);
    }
  }

  @bind
  private handleNoteAction(action: NoteActions): void {
    switch (action) {
      case NoteActions.CREATE_NOTE:
        this.createNote(NoteType.BLANK);
        break;
      case NoteActions.SIMPLIFY:
        this.createNote(NoteType.SIMPLIFY);
        break;
      case NoteActions.EXPLAIN:
        this.createNote(NoteType.EXPLAIN);
        break;
      case NoteActions.EXAMPLES:
        this.createNote(NoteType.EXAMPLES);
        break;
      case NoteActions.ASK:
        this.showAskActionView();
        break;
      case NoteActions.HIGHLIGHT:
        this.addOrChangeHighlightColor();
        break;
      case NoteActions.DELETE:
        this.removeHighlight();
        break;
      default:
        throw new Error(`Unsupported note action: ${action}`);
    }
  }

  @bind
  private handleAnonUser(controller: EditorController | undefined): void {
    controller?.closeBubbleMenu();
    openModal("login", {});
  }

  @bind
  private addOrChangeHighlightColor(_color?: HighlightColors): void {
    const controller = useEditorController();
    const color = _color ?? Store.editor.preferredHighlightColor.value;
    if (!Store.user.isLoggedIn.value) {
      this.handleAnonUser(controller);
      return;
    }
    controller?.addOrChangeHighlightColor(color);
  }

  @bind
  private removeHighlight(): void {
    const controller = useEditorController();
    controller?.removeHighlight(Store.editor.activeHighlightGroup.value);
  }

  @bind
  private showAskActionView() {
    const controller = useEditorController();
    if (!Store.user.isLoggedIn.value) {
      this.handleAnonUser(controller);
      return;
    }

    this.addOrChangeHighlightColor();
    this.changeView(NoteActions.ASK);
  }

  @bind
  private createNote(noteType: NoteType): void {
    const controller = useEditorController();

    if (!Store.user.isLoggedIn.value) {
      this.handleAnonUser(controller);
      return;
    }

    this.addOrChangeHighlightColor();
    controller?.addNote(noteType, Store.editor.activeHighlightGroup.value, {});
  }

  @bind
  private ask(query: string) {
    const controller = useEditorController();
    if (!Store.user.isLoggedIn.value) {
      this.handleAnonUser(controller);
      return;
    }
    this.addOrChangeHighlightColor();
    controller?.addNote(NoteType.ASK, Store.editor.activeHighlightGroup.value, { query });
  }

  @bind
  private changeView(action?: NoteActions): void {
    this.activeAction.value = action;

    if (action === NoteActions.ASK) {
      removeEventListener("keydown", this.handleKeyPress);
    }

    if (action == null) {
      addEventListener("keydown", this.handleKeyPress);
    }

    // Used by the bubble menu to update its position as the view can be a different width to the base toolbar
    this.dispatchEvent(new Event("viewchange"));
  }

  @bind
  renderActionView() {
    const activeAction = this.activeAction.value;

    switch (activeAction) {
      case NoteActions.ASK:
        return <AskView resetActiveAction={() => this.changeView(undefined)} onSubmit={this.ask} />;
      default:
        return null;
    }
  }

  @bind
  renderBaseView() {
    const hasActiveHighlight = Store.editor.activeHighlightGroup.value != null;

    return (
      <div class="p-1 justify-center items-center gap-1 flex animate-fadeInFast">
        <NWDropdown
          items={this.highlightOptions}
          limitHeight={false}
          openOrientation="top"
          position="absolute"
          customTrigger
          autoposition
        >
          <NWButton size="fit" type="text" icon="typeface">
            <div class="flex items-center">
              <span className="text-secondary">Highlight</span>
              <span className="text-quinary text-xs ml-2">{NoteActionToShortcut[NoteActions.HIGHLIGHT]}</span>
            </div>
          </NWButton>
        </NWDropdown>
        <NWButton size="fit" type="text" icon="message-square" onClick={() => this.handleNoteAction(NoteActions.ASK)}>
          <div class="flex items-center">
            <span className="text-secondary">Ask</span>
            <span className="text-quinary text-xs ml-2">{NoteActionToShortcut[NoteActions.ASK]}</span>
          </div>
        </NWButton>
        <NWDropdown
          items={this.noteOptions}
          width="fit"
          limitHeight={false}
          openOrientation="top"
          position="absolute"
          customTrigger
          horizontalOrientation="right"
          autoposition
        >
          <NWButton size="fit" type="text" icon="plus" iconSize="sm">
            <span className="text-secondary">Note</span>
          </NWButton>
        </NWDropdown>
        {hasActiveHighlight && (
          <NWButton size="fit" type="icon" icon="trash" iconOnly onClick={this.removeHighlight}></NWButton>
        )}
      </div>
    );
  }

  render() {
    const hasActionView = this.activeAction.value != null;

    return (
      <div
        className={classNames(
          "highlight-ensure-active shadow bg-white rounded-lg border border-gray-200 transition-[height, width] duration-300 ease-in-out",
          {
            "max-w-md w-screen h-fit": hasActionView,
            "h-12 w-fit flex items-center": !hasActionView,
          },
        )}
      >
        {hasActionView ? this.renderActionView() : this.renderBaseView()}
      </div>
    );
  }
}
